import { createSelector } from '@reduxjs/toolkit';
import { useAppSelector } from 'stores/hooks';
import { formatTimestampInTimezone, hoursThreeHourly } from 'utils/time';

/**
 * @typedef {import('../stores').AppState} State
 */

/** Filter weather days to 3-hourly timesteps (0, 3, 6, ..., 21). Safe for both hourly and 3-hourly source data. */
const filterWeatherDaysToThreeHourly = (days) => {
  if (!days) return days;
  return days.map((day) =>
    Array.isArray(day)
      ? day.filter(
          (step) =>
            step?.timestamp != null &&
            step?.utcOffset != null &&
            hoursThreeHourly.indexOf(
              formatTimestampInTimezone(step.timestamp, step.utcOffset, 'H'),
            ) >= 0,
        )
      : [],
  );
};

const mobileOrDesktopData = (days, mobile, extended) => {
  if (!days) return days;
  if (mobile && !extended) {
    return days.map((data) => data.filter((_, index) => index % 3 === 0));
  }
  return days;
};

export const getChartSunlightTimes = (state) => state.components.graphs.sunlightTimes;

export const getChartDaySummary = (state) => state.components.graphs.daySummary;

export const getChartSurf = createSelector(
  (state) => state.components.graphs.surf,
  (surf, mobile, extended) => ({
    ...surf,
    days: mobileOrDesktopData(surf.days, mobile, extended),
  }),
);

export const getChartSwells = createSelector(
  (state) => state.components.graphs.swells,
  (swells, mobile, extended) => ({
    ...swells,
    days: mobileOrDesktopData(swells.days, mobile, extended),
  }),
);

export const getChartTide = (state) => state.components.graphs.tide;

export const getChartWeather = createSelector(
  (state) => state.components.graphs.weather,
  (_, props) => props,
  (weather, { device: { mobile }, extended }) => ({
    ...weather,
    days: mobileOrDesktopData(weather.days, mobile, extended),
  }),
);

export const getChartWind = createSelector(
  (state) => state.components.graphs.wind,
  (_, props) => props,
  (wind, { device: { mobile }, extended }) => ({
    ...wind,
    days: mobileOrDesktopData(wind.days, mobile, extended),
  }),
);

/**
 * @param {State} state
 */
export const getRatings = (state) => state.components.graphs.ratings;

/**
 * @param {State} state
 */
export const getConsistency = (state) => state.components.graphs.consistency;

/**
 * @param {State} state
 */
export const getSwellSpectra = (state) => state.components.graphs.swellSpectra;

// Memoized selectors for specific configurations used in useGraphData
export const getChartSurfForDesktop = createSelector(
  (state) => state.components.graphs.surf,
  (surf) => ({
    ...surf,
    days: mobileOrDesktopData(surf.days, false, true),
  }),
);

export const getChartSwellsForDesktop = createSelector(
  (state) => state.components.graphs.swells,
  (swells) => ({
    ...swells,
    days: mobileOrDesktopData(swells.days, false, true),
  }),
);

export const getChartWaveEnergyForDesktop = createSelector(
  (state) => state.components.graphs.waveEnergy,
  (waveEnergy) => ({
    ...waveEnergy,
    days: mobileOrDesktopData(waveEnergy.days, false, true),
  }),
);

export const getChartWeatherForDesktop = createSelector(
  (state) => state.components.graphs.weather,
  (weather) => ({
    ...weather,
    days: filterWeatherDaysToThreeHourly(weather?.days),
  }),
);

export const getChartWindForDesktop = createSelector(
  (state) => state.components.graphs.wind,
  (wind) => ({
    ...wind,
    days: mobileOrDesktopData(wind.days, false, undefined),
  }),
);

/**
 * Raw weather from store (no 3-hourly filtering). Use for table so hourly view can show all 24 hours.
 * @param {State} state
 */
export const getWeatherRaw = (state) => state.components.graphs.weather;

export const useGraphData = () => ({
  consistency: useAppSelector(getConsistency),
  daySummary: useAppSelector(getChartDaySummary),
  ratings: useAppSelector(getRatings),
  sunlightTimes: useAppSelector(getChartSunlightTimes),
  surf: useAppSelector(getChartSurfForDesktop),
  swellSpectra: useAppSelector(getSwellSpectra),
  swells: useAppSelector(getChartSwellsForDesktop),
  waveEnergy: useAppSelector(getChartWaveEnergyForDesktop),
  tide: useAppSelector(getChartTide),
  weather: useAppSelector(getChartWeatherForDesktop),
  weatherRaw: useAppSelector(getWeatherRaw),
  wind: useAppSelector(getChartWindForDesktop),
});
